import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import { Button, Col, Form, Input, Select, Row, InputNumber, Space, Typography, Checkbox, Flex, TimePicker } from "antd"
import { MinusCircleOutlined } from "@ant-design/icons"

// Constants
import {
    RULE_PARAM_OPERATOR,
    OPERATOR_VALUE,
    RULE_PARAMS
} from './operator'

// Components
import showSecondarySidebar from "../../../../../../layout/MainLayout/helpers/showSecondarySidebar"
import Page from "../../../../../../components/Decoration/Page"

// Services Library
import { REDUX, TIME } from "../../../../../../services/util"

// Redux Actions
import * as REDUX_ACTIONS from '../../../../../../services/redux/actions'

// Style
import styles from '../../../../../VehicleManagement/modules/index.module.css'

const RuleCUForm = ({
    data = {}
}) => {

    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [ form ] = Form.useForm()

    const title = data?.title || 'Create'

    const uid = useSelector(REDUX.reduxStoreWrapper('user.uid'))
    const ruleTemplates = useSelector(REDUX.reduxStoreWrapper('ruleTemplates.byID'))
    const ruleParams = useSelector(REDUX.reduxStoreWrapper('rules.ruleParamByRULEPID'))
    const geofences = useSelector(REDUX.reduxStoreWrapper('geofences.byID'))

    const [ conditions, setConditions ] = useState(data?.conditions?.map(c => c.parameter) || [])

    const ruleTemplateOptions = Object.values(ruleTemplates)
    ?.filter(rt => rt.name !== 'DEFAULT')
    ?.map(rt => ({
        label: rt.name,
        value: rt.ruletid
    }))

    const geofenceOptions = Object.values(geofences)
    ?.filter(geofence => geofence.activeStatus === 1)
    ?.map(geofence => ({
        label: geofence.name,
        value: geofence.geoid
    }))

    const opO = Object.values(ruleParams).map(rp => ({
        label: rp.name,
        value: rp.rulepid
    }))

    const ruleParamOptions = [
        ...opO,
        { label: 'Geofence', value: 'geofence'},
        { label: 'Active Day', value: 'active_day'},
        { label: 'Active Time', value: 'active_time'},
    ]?.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))

    const operatorOptions = i => RULE_PARAM_OPERATOR[conditions[i]]?.map(p => ({
        label: p,
        value: OPERATOR_VALUE[p]
    }))

    const RuleTypeOptions = [ 
        { label: 'INFO', value: 0 },
        { label: 'WARNING', value: 1},
        { label: 'CRITICAL', value: 2 }
     ]

    const onFinish = e => {
        switch(title) {
            case 'Create': {
                dispatch(REDUX_ACTIONS.v3_rules.create_rule_request({
                    ...e,
                    buzzer: e.buzzer ? 1 : 0,
                    status: 1,
                    uid,
                }, () => {
                    navigate(`/management/rule`)
                }))
                break;
            }
            case 'Edit': {
                dispatch(REDUX_ACTIONS.v3_rules.update_rule_request({
                    ruleid: data.ruleid,
                    ruletid: data.ruletid,
                    ...e,
                    buzzer: e.buzzer ? 1 : 0,
                    status: data.status,
                    uid,
                }, () => {
                    navigate(`/management/rule`)
                }))
                break;
            }
        }
    }
    
    useEffect(() => {
        dispatch(REDUX_ACTIONS.v3_rules.get_rule_param_request())
    }, [])

    const TriggerValueComponent = (condition) => {

        let component = InputNumber
        let attributes = { min: 0 }

        const binaryVal = [ 0, 1 ]
        const style = { width: '100%' }
        const days =  [ 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday' ]
        const suffix = label => <span style={{ color: '#acacac'}}>{label}</span>

        switch(condition) {
            // case RULE_PARAMS.iBUTTON: 
            case RULE_PARAMS.MDVR_CONNECTIVITY_STATUS: 
            case RULE_PARAMS.CELL_CONNECTIVITY: 
            case RULE_PARAMS.IGNITION:
            case RULE_PARAMS.PANIC_DI1_LATCH:
            case RULE_PARAMS.GNSS_STATUS: {
                component = Select
                attributes = { options: binaryVal.map(bv => ({ label: bv, value: bv })) }
                break;
            }
            
            case RULE_PARAMS.HARSH_ACCELERATION: 
            case RULE_PARAMS.ACCELERATE: 
            case RULE_PARAMS.HARSH_BRAKING: {
                attributes = { suffix : suffix('m/s²') }
                break;
            }
            case RULE_PARAMS.HARSH_CORNERING: {
                attributes = { suffix : suffix('rad/s') }
                break;
            }
            case RULE_PARAMS.PARKING_TIME: 
            case RULE_PARAMS.IDLING_TIME: {
                attributes = { suffix : suffix('mins') }
                break;
            }
            case RULE_PARAMS.BATTERY_STATUS: {
                attributes = { suffix : suffix('%') }
                break;
            }
            case RULE_PARAMS.SPEED: {
                attributes = { suffix : suffix('km/h') }
                break;
            }
            
            case RULE_PARAMS.ACTIVE_DAY: {
                component = Select
                attributes = { options: days.map(day => ({ label: day, value: day })), mode: 'multiple' }
                break;
            }

            case RULE_PARAMS.ACTIVE_TIME: {
                component = TimePicker.RangePicker
                attributes = { format: 'HH:mm' }

                break;
            }

            case RULE_PARAMS.GEOFENCE: {
                component = Select
                attributes = {
                    mode: "multiple", 
                    options: geofenceOptions,
                    filterOption: (input, option) => (option?.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                }

                break;
            }

        }

        return React.createElement(component, { ...attributes, style })
    }

    
    const paramVal = (c) => {

        switch(c.parameter) {
            case RULE_PARAMS.ACTIVE_DAY: {
                return TIME.dayJS(TIME.isDayJS(c.value) && c.value)
            }

            case RULE_PARAMS.iBUTTON: {
                return '000000000000000'
            }

            default: 
                return c.value
        }
    }

    return (
        <div className="page-container">
            <Page title={`${title} Rule`}>
                <Form 
                    style={{ width: '50vw', minWidth: '60em', margin: '5em auto'}}
                    labelCol={{ span: 5 }}
                    form={form}
                    onFinish={onFinish}
                    initialValues={{
                        ...data,
                        conditions: data.conditions || [{}],
                    }}
                >
                    <div style={{ margin: '2em 0'}}>
                        <Typography.Text type="secondary">RULE INFORMATION.</Typography.Text>
                    </div>
                    <Form.Item
                        label="Rule Name"
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: 'Please input rule name!',
                            },
                        ]}
                    >
                        <Input size="large" placeholder="Rule Name" />
                    </Form.Item>
                    <Form.Item
                        label="Rule Type"
                        name="type_rule"
                        rules={[
                            {
                                required: true,
                                message: 'Please input rule type!',
                            },
                        ]}
                    >
                        <Select size="large" options={RuleTypeOptions} placeholder="Select a rule template" />
                    </Form.Item>
                    {
                        ruleTemplates[data.ruletid]?.name !== 'DEFAULT'
                        &&
                        <Form.Item
                            label="Rule Template Name"
                            name="ruletid"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please input rule template name!',
                                },
                            ]}
                        >
                            <Select size="large" options={ruleTemplateOptions} />
                        </Form.Item>
                    }
                    <Form.Item
                        label="Rule Compiled Name"
                        name="name_compiled"
                        // rules={[
                        //     {
                        //         required: true,
                        //         message: 'Please input rule compiled name!',
                        //     },
                        // ]}
                    >
                        <Input size="large" placeholder="Rule Compiled Name (Optional)" />
                    </Form.Item>
                    <Form.Item
                        label="Rule Violated Name"
                        name="name_violated"
                        // rules={[
                        //     {
                        //         required: true,
                        //         message: 'Please input rule violated name!',
                        //     },
                        // ]}
                    >
                        <Input size="large" placeholder="Rule Violated Name (Optional)" />
                    </Form.Item>
                    <Form.Item
                        label="Remote Buzzer Activation"
                    >
                        <Flex justify="space-between" >
                        <Form.Item name='buzzer' valuePropName="checked">
                            <Checkbox style={{ marginTop: '-1.6em'}} />
                        </Form.Item>
                        <Form.Item 
                            label="Buzzer Duration"
                            name='buzzer_duration'
                            style={{ float: 'right'}}
                        >
                            
                            <InputNumber 
                                min={0} 
                                style={{ width: '20em' }} 
                                suffix={<span style={{ color: '#acacac'}}>seconds</span>}
                                onKeyPress={(e) => !/[0-9.]/.test(e.key) && e.preventDefault()}
                            />
                        </Form.Item>

                        </Flex>
                    </Form.Item>

                    <Form.Item label="Trigger Condition">
                        <Form.List name="conditions" >
                            {(fields, opt) => 
                                <Row gutter={16} style={{ margin: '8px 0'}}>
                                    {fields.map((field, i) => (
                                        <Col span={12} key={i}>
                                            <div>
                                                <label htmlFor={[field.name, 'parameter']}>Trigger Type: </label>
                                                <Form.Item name={[field.name, 'parameter']} noStyle>
                                                    <Select options={ruleParamOptions} 
                                                        showSearch
                                                        onChange={e => {
                                                            const conditionsForm = form.getFieldsValue().conditions
                                                            const opVal = RULE_PARAM_OPERATOR[e]?.[0] || ''

                                                            form.setFieldsValue({ 
                                                                conditions: conditionsForm.map((c, ic) => ic === i ? ({ 
                                                                    ...c, 
                                                                    operator: OPERATOR_VALUE[opVal], 
                                                                    value: paramVal(c)
                                                                }) : c) 
                                                            })

                                                            conditions[i] = e
                                                            setConditions(conditions)
                                                        }}
                                                    />
                                                </Form.Item>
                                            </div>
                                            <div>
                                                <label htmlFor={[field.name, 'operator']}>Trigger Operator: </label>
                                                <Form.Item name={[field.name, 'operator']} noStyle>
                                                    <Select options={operatorOptions(i)} />
                                                </Form.Item>
                                            </div>
                                            <div>
                                                {
                                                    !(conditions[i] === RULE_PARAMS.iBUTTON)
                                                    &&
                                                    <label htmlFor={[field.name, 'parameter']} style={{ display: ''}}>Trigger Value: </label>
                                                }
                                                <Form.Item name={[field.name, 'value']} style={{ display: (conditions[i] === RULE_PARAMS.iBUTTON) && 'none' }}> 
                                                    { TriggerValueComponent(conditions[i]) }
                                                </Form.Item>
                                            </div>

                                            <Space style={{ margin: '1em 0'}}>
                                                <Button type="dashed" onClick={() => opt.remove(field.name)} style={{ borderColor: 'red', color: 'red' }} icon={<MinusCircleOutlined />}>
                                                    Remove
                                                </Button>
                                                <Button type="dashed" onClick={() => opt.add()}>
                                                    Add
                                                </Button>
                                            </Space>
                                        </Col>
                                    ))}
                                    {
                                        fields.length === 0
                                        &&
                                        <Button type="dashed" onClick={() => opt.add()}>
                                            + Add Fuel Profile
                                        </Button>     
                                    }
                                </Row>
                            }
                        </Form.List>
                    </Form.Item>

                    <div className={styles['button-container']}>
                        <Button className={`${styles['button']} ${styles['cancel']}`} onClick={() => navigate(-1)}>Cancel</Button>
                        <Button className={`${styles['button']} ${styles['add']}`} htmlType="submit">{title === 'Create' ? 'Add' : 'Save'}</Button>
                    </div>
                </Form>
            </Page>
        </div>
    )
}

const MemoizedRuleCUForm = React.memo(RuleCUForm);

export default showSecondarySidebar(false)(MemoizedRuleCUForm);